import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import { formatDistanceToNow } from 'date-fns';

const NotificationList = ({ notifications, onMarkAsRead, onDelete }) => {
  const navigate = useNavigate();

  const getPriorityVariant = (priority) => {
    switch (priority) {
      case 'critical':
        return 'danger';
      case 'high':
        return 'warning';
      case 'medium':
        return 'default';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'expiry_alert':
        return 'Clock';
      case 'vacancy_alert':
        return 'Home';
      case 'lead_sla_alert':
        return 'UserCheck';
      case 'renewal_reminder':
        return 'Calendar';
      case 'email_trigger':
        return 'Mail';
      case 'system_alert':
        return 'Bell';
      default:
        return 'Bell';
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification?.isRead) {
      onMarkAsRead(notification?.id);
    }
    if (notification?.actionUrl) {
      navigate(notification?.actionUrl);
    }
  };

  if (notifications?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <Icon name="Bell" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No notifications</h3>
        <p className="text-muted-foreground">You're all caught up!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {notifications?.map((notification) => (
        <div
          key={notification?.id}
          className={`bg-card rounded-lg border ${
            notification?.isRead ? 'border-border' : 'border-primary-300 bg-primary-50'
          } p-4 transition-all hover:shadow-md cursor-pointer`}
          onClick={() => handleNotificationClick(notification)}
        >
          <div className="flex items-start gap-4">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                notification?.isRead ? 'bg-gray-100' : 'bg-primary-100'
              }`}
            >
              <Icon
                name={getTypeIcon(notification?.type)}
                size={20}
                className={notification?.isRead ? 'text-gray-600' : 'text-primary-600'}
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3
                  className={`font-semibold ${
                    notification?.isRead ? 'text-foreground' : 'text-primary-900'
                  }`}
                >
                  {notification?.title}
                </h3>
                <Badge variant={getPriorityVariant(notification?.priority)} size="sm">
                  {notification?.priority}
                </Badge>
              </div>

              <p className="text-sm text-muted-foreground mb-2">{notification?.message}</p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>
                    {formatDistanceToNow(new Date(notification?.createdAt), { addSuffix: true })}
                  </span>
                  {notification?.entityName && (
                    <>
                      <span>•</span>
                      <span>{notification?.entityName}</span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {!notification?.isRead && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onMarkAsRead(notification?.id);
                      }}
                      iconName="Check"
                      iconPosition="left"
                    >
                      Mark Read
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onDelete(notification?.id);
                    }}
                    iconName="Trash2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationList;